import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { USER_APPROVE_STATUS } from '@/config/constants';
import {
  useGetEmailPreferencesQuery,
  useUpdateEmailPreferencesMutation,
} from '@/apis/extended/userPreferencesApi';
import { Loader } from '@/components/Layout/Loader';
import { useAuth } from '@/hooks/useAuth';

const contractStages = [
  { key: 'draft', label: 'Draft' },
  { key: 'negotiating', label: 'Negotiating' },
  { key: 'awaiting_signatures', label: 'Awaiting Signatures' },
  { key: 'ratified', label: 'Ratified' },
  { key: 'inspection_pending', label: 'Inspection Pending' },
  { key: 'repair_requested', label: 'Repair Requested' },
  { key: 'repair_negotiating', label: 'Repair Negotiating' },
  { key: 'escrow', label: 'Escrow' },
  { key: 'completed', label: 'Completed' },
];

export const EmailPreferences = () => {
  const { user } = useAuth();
  const showContractEmailPrefs =
    user?.approveStatus === USER_APPROVE_STATUS.APPROVED;

  const { data: preferences, isLoading } = useGetEmailPreferencesQuery();
  const [updatePreferences, { isLoading: isUpdating }] =
    useUpdateEmailPreferencesMutation();

  const [localPreferences, setLocalPreferences] = useState({
    contract: {
      enabled: true,
      stages: {
        draft: true,
        negotiating: true,
        awaiting_signatures: true,
        ratified: true,
        inspection_pending: true,
        repair_requested: true,
        repair_negotiating: true,
        escrow: true,
        completed: true,
      },
    },
    interestRate: { enabled: true },
    system: { enabled: true },
    marketing: { enabled: false },
    shopping: { enabled: true },
  });

  useEffect(() => {
    if (preferences) {
      setLocalPreferences(preferences);
    }
  }, [preferences]);

  const handleCategoryToggle = (category: string, enabled: boolean) => {
    setLocalPreferences({
      ...localPreferences,
      [category]: {
        ...localPreferences[category as keyof typeof localPreferences],
        enabled,
      },
    });
  };

  const handleStageToggle = (stage: string, enabled: boolean) => {
    setLocalPreferences({
      ...localPreferences,
      contract: {
        ...localPreferences.contract,
        stages: {
          ...localPreferences.contract.stages,
          [stage]: enabled,
        },
      },
    });
  };

  const handleSave = async () => {
    try {
      await updatePreferences(localPreferences).unwrap();
      toast.success('Email preferences updated successfully');
    } catch (error) {
      toast.error('Failed to update email preferences');
      console.error('Error updating preferences:', error);
    }
  };

  if (isLoading) {
    return <Loader isFull />;
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 3 }}>
        Email Notification Preferences
      </Typography>

      <Card>
        <CardContent>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid
              item
              xs={12}
              md={showContractEmailPrefs ? 4 : 12}
              lg={showContractEmailPrefs ? 3 : 12}
              sx={{
                maxWidth: showContractEmailPrefs ? { md: 420 } : undefined,
                order: { xs: showContractEmailPrefs ? 2 : 1, md: 1 },
              }}
            >
              <Box sx={{ mb: 2.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPreferences.interestRate.enabled}
                      onChange={(e) =>
                        handleCategoryToggle('interestRate', e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight={600}>
                      Interest Rate Updates
                    </Typography>
                  }
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 4, display: 'block' }}
                >
                  Receive emails when interest rates are updated
                </Typography>
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPreferences.system.enabled}
                      onChange={(e) =>
                        handleCategoryToggle('system', e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight={600}>
                      System Notifications
                    </Typography>
                  }
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 4, display: 'block' }}
                >
                  Receive important system updates and account-related emails
                </Typography>
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPreferences.marketing.enabled}
                      onChange={(e) =>
                        handleCategoryToggle('marketing', e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight={600}>
                      Marketing Emails
                    </Typography>
                  }
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 4, display: 'block' }}
                >
                  Receive promotional emails and special offers
                </Typography>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPreferences.shopping?.enabled !== false}
                      onChange={(e) =>
                        handleCategoryToggle('shopping', e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="subtitle1" fontWeight={600}>
                      Shopping Notifications
                    </Typography>
                  }
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 4, display: 'block' }}
                >
                  Receive alerts about saved properties, price changes, new
                  listings, and open houses
                </Typography>
              </Box>
            </Grid>

            {showContractEmailPrefs && (
              <Grid item xs={12} md={8} lg={9} sx={{ order: { xs: 1, md: 2 } }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localPreferences.contract.enabled}
                      onChange={(e) =>
                        handleCategoryToggle('contract', e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="h6">Contract Notifications</Typography>
                  }
                />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ ml: 4, mb: 2 }}
                >
                  Receive emails about contract status changes, proposals, and
                  negotiations
                </Typography>

                {localPreferences.contract.enabled && (
                  <Box sx={{ ml: 4, mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Contract Stages:
                    </Typography>
                    <FormGroup>
                      {contractStages.map((stage) => (
                        <FormControlLabel
                          key={stage.key}
                          control={
                            <Checkbox
                              checked={
                                localPreferences.contract.stages[
                                  stage.key as keyof typeof localPreferences.contract.stages
                                ] || false
                              }
                              onChange={(e) =>
                                handleStageToggle(stage.key, e.target.checked)
                              }
                            />
                          }
                          label={stage.label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isUpdating}
              sx={{ minWidth: 120 }}
            >
              {isUpdating ? 'Saving...' : 'Save Preferences'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
